<template>
  <el-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    @open="onOpen"
    @close="onClose"
    title="Audio Devices"
    width="500px"
    append-to-body
  >
    <el-alert
      v-if="!isOutputSelectionSupported"
      type="warning"
      :closable="false"
      show-icon
      title="This browser can't choose audio outputs. All sounds play on the system default device. Use Chrome, Edge or Firefox to pick devices."
    ></el-alert>
    <el-form v-else label-position="top">
      <el-form-item label="Ring on">
        <el-row :gutter="10" style="width: 100%">
          <el-col :span="18">
            <el-select
              :model-value="ringSinkIds"
              @update:model-value="setRingSinkIds"
              multiple
              placeholder="System default"
              style="width: 100%"
            >
              <el-option
                v-for="d in deviceOptions"
                :key="d.deviceId"
                :label="d.label"
                :value="d.deviceId"
              ></el-option>
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-button @click="toggleTest('ring')" style="width: 100%">
              {{ testing == 'ring' ? 'Stop' : 'Test' }}
            </el-button>
          </el-col>
        </el-row>
        <small>
          Incoming calls and status warnings. Pick more than one to ring on
          all of them, e.g. speakers and headset.
        </small>
      </el-form-item>
      <el-form-item label="Call audio on">
        <el-row :gutter="10" style="width: 100%">
          <el-col :span="18">
            <el-select
              :model-value="callSinkId"
              @update:model-value="setCallSinkId"
              placeholder="System default"
              style="width: 100%"
            >
              <el-option
                v-for="d in deviceOptions"
                :key="d.deviceId"
                :label="d.label"
                :value="d.deviceId"
              ></el-option>
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-button @click="toggleTest('call')" style="width: 100%">
              {{ testing == 'call' ? 'Stop' : 'Test' }}
            </el-button>
          </el-col>
        </el-row>
        <small>The caller's voice and outbound ringing.</small>
      </el-form-item>
    </el-form>
    <el-divider></el-divider>
    <el-form label-position="top">
      <el-form-item label="Microphone">
        <el-row :gutter="10" style="width: 100%" align="middle">
          <el-col :span="18">
            <el-select
              :model-value="
                micDeviceId == DEFAULT_SINK ? undefined : micDeviceId
              "
              @update:model-value="setMicDeviceId"
              placeholder="No microphones found"
              style="width: 100%"
            >
              <el-option
                v-for="d in micOptions"
                :key="d.deviceId"
                :label="d.label"
                :value="d.deviceId"
              ></el-option>
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-button @click="toggleMicTest()" style="width: 100%">
              {{ micStream ? 'Stop' : 'Test' }}
            </el-button>
          </el-col>
        </el-row>
        <el-row style="width: 100%; margin-top: 8px">
          <el-col :span="18">
            <el-progress
              :percentage="micLevel"
              :status="micHeard ? 'success' : undefined"
              :show-text="false"
              :stroke-width="12"
            ></el-progress>
          </el-col>
        </el-row>
        <small v-if="micError" style="color: var(--el-color-danger)">
          {{ micError }}
        </small>
        <small
          v-else-if="micStream && micMuted"
          style="color: var(--el-color-warning)"
        >
          {{ micLabel }} is muted by your system or another app. Check the mute
          switch on the headset and your system sound settings.
        </small>
        <small v-else-if="micStream && micHeard">
          Audio is being received from {{ micLabel }}.
        </small>
        <small
          v-else-if="micStream && micSilent"
          style="color: var(--el-color-warning)"
        >
          No audio from {{ micLabel }}. Check that it isn't muted or pick
          another microphone.
        </small>
        <small v-else-if="micStream">
          Listening on {{ micLabel }}. Say something...
        </small>
        <small v-else>Your voice on calls. Press Test to check it.</small>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="refreshAudioDevices(true)"
        >Refresh devices</el-button
      >
      <el-button v-if="hasCustomDevices" @click="resetAudioDevices"
        >Reset to default</el-button
      >
      <el-button type="primary" @click="$emit('update:modelValue', false)"
        >Done</el-button
      >
    </template>
  </el-dialog>
</template>
<script>
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';
import { DEFAULT_SINK, MultiSinkAudio } from 'src/util/audioDevices';
import { useAudioDevices } from 'src/composables/useAudioDevices';

const RING_URL =
  'https://adbase-static.s3.amazonaws.com/sounds/telephone_digital_ring.mp3';
const TEST_LENGTH_MS = 4000;
// Level (0-100) that counts as hearing the mic, above background hiss
const MIC_HEARD_LEVEL = 8;
// Warn if nothing is heard within this time
const MIC_SILENT_MS = 5000;
// Stop the mic test on its own so the mic isn't left open
const MIC_TEST_LENGTH_MS = 30000;

const toOptions = (devices, name) => {
  const options = devices
    .filter((d) => d.deviceId)
    .map((d, i) => ({
      deviceId: d.deviceId,
      label: d.label || `${name} ${i + 1}`,
    }));
  // Chromium already lists a 'default' device, other browsers don't
  if (!options.some((d) => d.deviceId == DEFAULT_SINK)) {
    options.unshift({ deviceId: DEFAULT_SINK, label: 'System default' });
  }
  return options;
};

export default {
  name: 'AudioDeviceSettings',
  props: {
    modelValue: Boolean,
  },
  emits: ['update:modelValue'],
  data() {
    return {
      DEFAULT_SINK,
      testing: undefined, // 'ring' | 'call'
      testAudio: undefined,
      testTimeout: undefined,
      // Mic test
      micStream: undefined,
      micLabel: undefined,
      micLevel: 0,
      micHeard: false,
      micSilent: false,
      micError: undefined,
      micMuted: false,
    };
  },
  setup() {
    return useAudioDevices();
  },
  computed: {
    ...mapGetters('appConfig', ['getAppConfig']),
    deviceOptions() {
      return toOptions(this.outputDevices, 'Output');
    },
    micOptions() {
      // Real mics only, no default alias (it may not be a working input)
      return this.micDevices.map((d, i) => ({
        deviceId: d.deviceId,
        label: d.label || `Microphone ${i + 1}`,
      }));
    },
  },
  watch: {
    // Keep a running test on the selected devices
    ringSinkIds(ids) {
      if (this.testing == 'ring') {
        this.testAudio?.setSinks(ids);
      }
    },
    callSinkId(id) {
      if (this.testing == 'call') {
        this.testAudio?.setSinks([id]);
      }
    },
    // Restart a running mic test on the newly picked mic
    micDeviceId() {
      if (this.micStream) {
        this.stopMicTest();
        this.startMicTest();
      }
    },
  },
  methods: {
    onOpen() {
      // Ask for mic permission if needed so we can show device names
      this.refreshAudioDevices(true);
    },
    onClose() {
      this.stopTest();
      this.stopMicTest();
    },
    toggleMicTest() {
      if (this.micStream) {
        this.stopMicTest();
        return;
      }
      this.startMicTest();
    },
    async startMicTest() {
      this.micError = undefined;
      this.micHeard = false;
      this.micSilent = false;
      this.micLevel = 0;
      // Create the AudioContext while we still have the click's user activation,
      // otherwise the browser can leave it suspended and the meter reads silence
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const context = new AudioCtx();
      const resumed = context.resume().catch(() => {});
      // Same processing the softphone uses so the test matches a real call
      const constraints = (deviceId) => ({
        audio: {
          deviceId,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      const micId = this.micDeviceId;
      let stream;
      try {
        // Require the picked mic so we test that one and not whatever Chrome prefers
        stream = await navigator.mediaDevices
          .getUserMedia(
            constraints(micId == DEFAULT_SINK ? undefined : { exact: micId })
          )
          .catch((e) => {
            // Picked mic is gone, test the default instead
            if (e.name == 'OverconstrainedError') {
              return navigator.mediaDevices.getUserMedia(constraints());
            }
            throw e;
          });
      } catch (e) {
        this.micError =
          e.name == 'NotAllowedError'
            ? 'Microphone access is blocked. Allow it in the browser address bar and try again.'
            : e.name == 'NotFoundError'
            ? 'No microphone found. Plug one in and try again.'
            : 'Unable to open the microphone: ' + (e.message || e.name);
        context.close();
        return;
      }
      // Dialog may have closed while we waited on permission
      if (!this.modelValue) {
        stream.getTracks().forEach((t) => t.stop());
        context.close();
        return;
      }

      const track = stream.getAudioTracks()[0];
      this.micStream = markRaw(stream);
      this.micLabel = track?.label || 'your microphone';
      track?.addEventListener('ended', () => {
        this.stopMicTest();
        this.micError = 'The microphone was disconnected.';
      });
      // Muted means the OS, hardware switch or another app is blocking the input
      this.micMuted = !!track?.muted;
      track?.addEventListener('mute', () => (this.micMuted = true));
      track?.addEventListener('unmute', () => (this.micMuted = false));

      this.micContext = context;
      // Keep references to the nodes. Firefox garbage collects an unreferenced
      // MediaStreamSource and the analyser then only sees silence
      this.micSource = context.createMediaStreamSource(stream);
      this.micAnalyser = context.createAnalyser();
      this.micAnalyser.fftSize = 1024;
      // Some browsers only process nodes that lead to the output, so connect it
      // through a muted gain to keep the graph running without echoing the mic
      const sink = context.createGain();
      sink.gain.value = 0;
      this.micSource.connect(this.micAnalyser);
      this.micAnalyser.connect(sink);
      sink.connect(context.destination);
      await resumed;
      console.info('Mic test', {
        requested: micId,
        label: track?.label,
        readyState: track?.readyState,
        muted: track?.muted,
        enabled: track?.enabled,
        settings: track?.getSettings?.(),
        contextState: context.state,
      });
      if (context.state != 'running') {
        this.micError =
          'The browser blocked audio processing. Press Test again to retry.';
      }
      const analyser = this.micAnalyser;
      const samples = new Float32Array(analyser.fftSize);

      const tick = () => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const s of samples) {
          sum += s * s;
        }
        // RMS is small for speech (~0.01-0.2), scale so normal talking fills the bar
        const rms = Math.sqrt(sum / samples.length);
        const level = Math.min(100, Math.round(rms * 400));
        // Smooth the fall so the bar doesn't flicker
        this.micLevel = Math.max(level, Math.round(this.micLevel * 0.85));
        if (level >= MIC_HEARD_LEVEL) {
          this.micHeard = true;
          this.micSilent = false;
        }
        this.micFrame = requestAnimationFrame(tick);
      };
      tick();

      this.micSilentTimeout = setTimeout(() => {
        this.micSilent = !this.micHeard;
      }, MIC_SILENT_MS);
      this.micStopTimeout = setTimeout(
        () => this.stopMicTest(),
        MIC_TEST_LENGTH_MS
      );
    },
    stopMicTest() {
      cancelAnimationFrame(this.micFrame);
      clearTimeout(this.micSilentTimeout);
      clearTimeout(this.micStopTimeout);
      this.micFrame = this.micSilentTimeout = this.micStopTimeout = undefined;
      this.micStream?.getTracks().forEach((t) => t.stop());
      this.micStream = undefined;
      this.micSource?.disconnect();
      this.micSource = this.micAnalyser = undefined;
      this.micContext?.close();
      this.micContext = undefined;
      this.micLevel = 0;
      this.micMuted = false;
    },
    toggleTest(type) {
      const wasTesting = this.testing;
      this.stopTest();
      if (wasTesting == type) {
        return;
      }
      const ringUrl = this.getAppConfig?.callCenter?.ringAudio ?? RING_URL;
      const sinks =
        type == 'ring' ? this.ringSinkIds : [this.callSinkId];
      this.testAudio = markRaw(new MultiSinkAudio(ringUrl, sinks));
      this.testing = type;
      this.testAudio.play();
      this.testTimeout = setTimeout(() => this.stopTest(), TEST_LENGTH_MS);
    },
    stopTest() {
      if (this.testTimeout) {
        clearTimeout(this.testTimeout);
        this.testTimeout = undefined;
      }
      this.testAudio?.destroy();
      this.testAudio = undefined;
      this.testing = undefined;
    },
  },
  beforeUnmount() {
    this.stopTest();
    this.stopMicTest();
  },
};
</script>
